import { useCallback, useContext, useEffect, useState } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import BryntumContext from "@/react/context/bryntumContext";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { Eye, EyeOff, Info, Key, Trash2 } from "lucide-react";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { Button } from "@/modern/components/ui/button";
import { Card } from "@/modern/components/ui/card";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import { toast } from "sonner";
import { useBoardPermissions } from "../hooks/useBoardPermissions";
import { IntegrationAlert, OPENAI_KEY_REQUIRED_TITLE } from "@/integrations";

const OpenAIDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project } = useContext(BryntumContext);
  const [apiKey, setApiKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const loadApiKey = useCallback(() => {
    // @ts-expect-error - userPreferences is dynamically set in bryntumContext
    const currentKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.openAiApiKey, null, true);
    setApiKey(currentKey || "");
  }, []);

  // Subscribe to board preferences changes to trigger rerender if open api key flag changes
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      if (
        record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey ||
        record.key === PERSISTENCE_STATE_KEYS.openAiApiKey
      ) {
        loadApiKey();
        return true;
      }
      return false;
    },
  });

  useEffect(() => {
    loadApiKey();
  }, [loadApiKey]);

  // @ts-expect-error - userPreferences is dynamically set in bryntumContext
  const hasOpenAIKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";

  const { canWrite } = useBoardPermissions();

  const handleSaveApiKey = async () => {
    if (!project) return;

    if (!canWrite) {
      toast.error("You don't have permission to update the OpenAI API key");
      return;
    }

    if (!apiKey || apiKey.trim() === "") {
      toast.error("Please enter an API key or use the Remove button to delete it");
      return;
    }

    setIsSaving(true);
    try {
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.openAiApiKey, apiKey, true);
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "true", true);
      // @ts-expect-error - project type not fully defined
      await project.sync();

    } catch (error) {
      console.error("Error saving OpenAI API key:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveKey = async () => {
    if (!project) return;

    if (!canWrite) {
      toast.error("You don't have permission to remove the OpenAI API key");
      return;
    }

    if (!hasOpenAIKey) {
      toast.error("No API key to remove");
      return;
    }

    setIsSaving(true);
    try {
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.deleteSetting(PERSISTENCE_STATE_KEYS.openAiApiKey, true);
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true);
      // @ts-expect-error - project type not fully defined
      await project.sync();

      setApiKey("");
      toast.success("OpenAI API key removed successfully");
    } catch (error) {
      console.error("Error removing OpenAI API key:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      {canWrite ? (
        <Card className="py-3 px-4 mb-6 max-w-3xl">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Key className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1">
                  {hasOpenAIKey ? "OpenAI API Key Configured" : "Configure OpenAI API Key"}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {hasOpenAIKey
                    ? "Your OpenAI API key is configured. Enter a new key to update it, or remove it below."
                    : "Enter your OpenAI API key to enable AI-powered features in your board."}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="openai-api-key" className="text-sm font-medium">
                  OpenAI API Key
                </Label>
                {apiKey ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSaving}
                    className="h-7 px-2 text-xs"
                  >
                    {showPassword ? (
                      <>
                        <EyeOff className="h-3 w-3 mr-1" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Eye className="h-3 w-3 mr-1" />
                        Show
                      </>
                    )}
                  </Button>
                ) : null}
              </div>
              <Input
                id="openai-api-key"
                type={showPassword ? "text" : "password"}
                placeholder="sk-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={isSaving}
                autoComplete="one-time-code"
                data-form-type="other"
                className="font-mono text-sm"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              {hasOpenAIKey ? (
                <Button
                  variant="destructive"
                  onClick={handleRemoveKey}
                  disabled={isSaving}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Remove Key
                </Button>
              ) : null}
              <Button onClick={handleSaveApiKey} disabled={isSaving || !apiKey.trim()} size="sm">
                {isSaving ? (hasOpenAIKey ? "Updating..." : "Saving...") : hasOpenAIKey ? "Update Key" : "Save Key"}
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {!hasOpenAIKey && (
            <IntegrationAlert
              className="mb-6"
              variant="missing_config"
              provider="openai"
              title={OPENAI_KEY_REQUIRED_TITLE}
              message="To configure AI Prompt settings, you need to set up your OpenAI API key first. Only the Board Owner can configure the API key."
            />
          )}

          {hasOpenAIKey && (
            <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Key className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-foreground mb-1">OpenAI API Key Configured</h4>
                  <p className="text-sm text-foreground">Your OpenAI API key is configured and ready to use.</p>
                </div>
              </div>
            </div>
          )}

          <Card className="py-3 px-4 mb-6 max-w-3xl border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/10">
            <div className="flex gap-3">
              <Info className="h-4 w-4 text-gray-600 dark:text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-900 dark:text-gray-100">
                Only the <strong>Board Owner</strong> can view, update, or remove the OpenAI API key.
              </p>
            </div>
          </Card>
        </>
      )}

      {/* Information Section */}
      <div className="mt-6 p-4 bg-card rounded-lg border border-border max-w-3xl">
        <h4 className="text-sm font-semibold text-foreground mb-2">About OpenAI Integration</h4>
        <p className="text-sm text-muted-foreground leading-relaxed mb-3">The OpenAI API key enables AI-powered features such as:</p>
        <ul className="text-sm text-muted-foreground space-y-1 ml-4">
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>AI-generated image inspection and analysis</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>Intelligent context-based recommendations</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            <span>Automated content generation based on your custom prompts</span>
          </li>
        </ul>
        <p className="text-xs text-muted-foreground mt-4">
          <strong>Security Note:</strong> Your API key is stored securely and can only be viewed and managed by the Board Owner.
        </p>
      </div>
    </div>
  );
};

export default OpenAIDetail;
